import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, ImageOff, Loader2, Package } from "lucide-react";
import api from "../lib/api";
import { CATEGORIES } from "../lib/constants";
import Header from "../components/Header";
import Footer from "../components/Footer";
import FloatingWhatsApp from "../components/FloatingWhatsApp";
import ProductDetailModal from "../components/ProductDetailModal";
import { Toaster } from "sonner";

export default function CategoryPage() {
  const { categoryId } = useParams();
  const nav = useNavigate();
  const category = CATEGORIES.find((c) => c.id === categoryId);

  const [subs, setSubs] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSub, setActiveSub] = useState("all");
  const [selected, setSelected] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const [s, p] = await Promise.all([
          api.get(`/api/subcategories?category_id=${categoryId}`),
          api.get(
            `/api/products?category_id=${categoryId}&include_image=true`
          ),
        ]);
        if (!alive) return;
        setSubs(s.data || []);
        setProducts(p.data || []);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [categoryId]);

  const openProduct = async (product) => {
    // Open immediately with what we have, then load full details (incl. PDF)
    setSelected(product);
    if (product.tech_sheet_pdf !== undefined) return;
    try {
      setLoadingDetail(true);
      const { data } = await api.get(`/api/products/${product.id}`);
      setSelected(data);
    } catch {
      // keep partial product on error
    } finally {
      setLoadingDetail(false);
    }
  };

  const filtered = useMemo(() => {
    if (activeSub === "all") return products;
    if (activeSub === "none") return products.filter((p) => !p.subcategory_id);
    return products.filter((p) => p.subcategory_id === activeSub);
  }, [products, activeSub]);

  const productsBySub = useMemo(() => {
    const map = {};
    for (const p of products) {
      const key = p.subcategory_id || "none";
      if (!map[key]) map[key] = 0;
      map[key]++;
    }
    return map;
  }, [products]);

  const onCotizar = (product) => {
    nav(`/?prefill=${encodeURIComponent(product.name)}#contacto`);
  };

  if (!category) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-display text-3xl text-pv-green">
            Categoría no encontrada
          </h1>
          <Link
            to="/"
            className="mt-4 inline-block text-pv-orange font-semibold"
          >
            ← Volver al inicio
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />
      <main className="pt-24 lg:pt-28">
        {/* Breadcrumb / hero */}
        <section className="bg-white border-b border-slate-200">
          <div className="mx-auto max-w-7xl px-5 lg:px-8 py-10 lg:py-14">
            <Link
              to="/#catalogo"
              data-testid="back-to-catalog"
              className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-pv-green mb-4 transition-colors"
            >
              <ArrowLeft size={14} /> Volver al catálogo
            </Link>
            <div className="overline text-pv-orange mb-3">
              Categoría {category.code}
            </div>
            <h1 className="font-display font-extrabold text-pv-green text-4xl sm:text-5xl lg:text-6xl tracking-tight">
              {category.name}
            </h1>
            <p className="mt-3 text-slate-600 text-base lg:text-lg">
              {products.length}{" "}
              {products.length === 1 ? "producto disponible" : "productos disponibles"}
              {subs.length > 0 ? ` en ${subs.length} subcategorías` : ""}.
            </p>
          </div>
        </section>

        {/* Subcategory chips */}
        {subs.length > 0 && (
          <section className="bg-slate-50 border-b border-slate-200 sticky top-16 lg:top-20 z-20">
            <div className="mx-auto max-w-7xl px-5 lg:px-8 py-4 flex gap-2 overflow-x-auto">
              <SubChip
                label={`Todos (${products.length})`}
                active={activeSub === "all"}
                onClick={() => setActiveSub("all")}
                testId="sub-chip-all"
              />
              {subs.map((s) => (
                <SubChip
                  key={s.id}
                  label={`${s.name} (${productsBySub[s.id] || 0})`}
                  active={activeSub === s.id}
                  onClick={() => setActiveSub(s.id)}
                  testId={`sub-chip-${s.id}`}
                />
              ))}
              {productsBySub.none > 0 && (
                <SubChip
                  label={`Sin subcategoría (${productsBySub.none})`}
                  active={activeSub === "none"}
                  onClick={() => setActiveSub("none")}
                  testId="sub-chip-none"
                />
              )}
            </div>
          </section>
        )}

        {/* Products grid */}
        <section className="py-14 lg:py-20">
          <div className="mx-auto max-w-7xl px-5 lg:px-8">
            {loading ? (
              <div className="py-24 text-center text-slate-400">
                <Loader2 size={32} className="mx-auto animate-spin mb-3" />
                Cargando productos...
              </div>
            ) : filtered.length === 0 ? (
              <EmptyState category={category} />
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 lg:gap-6">
                {filtered.map((p) => (
                  <ProductCard
                    key={p.id}
                    product={p}
                    onClick={() => openProduct(p)}
                  />
                ))}
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
      <FloatingWhatsApp />
      <Toaster position="top-right" richColors closeButton />

      <ProductDetailModal
        product={selected}
        onClose={() => setSelected(null)}
        onCotizar={onCotizar}
      />
    </div>
  );
}

function SubChip({ label, active, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`shrink-0 px-4 py-2 text-sm font-semibold border rounded-sm transition-all ${
        active
          ? "bg-pv-green text-white border-pv-green"
          : "bg-white text-slate-700 border-slate-300 hover:border-pv-green"
      }`}
    >
      {label}
    </button>
  );
}

function ProductCard({ product, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`product-card-${product.id}`}
      className="group bg-white border border-slate-200 rounded-sm overflow-hidden text-left hover:border-pv-lime hover:-translate-y-1 transition-all duration-300 flex flex-col"
    >
      <div className="aspect-square bg-slate-50 flex items-center justify-center overflow-hidden">
        {product.image ? (
          <img
            src={product.image}
            alt={product.name}
            className="w-full h-full object-contain p-3 group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="text-slate-300 flex flex-col items-center">
            <ImageOff size={36} />
            <span className="text-xs mt-2">Sin foto</span>
          </div>
        )}
      </div>
      <div className="p-4 flex-1 flex flex-col">
        {product.brand && (
          <div className="overline text-pv-orange mb-1">{product.brand}</div>
        )}
        <h3 className="font-display font-bold text-pv-green text-base leading-tight tracking-tight line-clamp-2">
          {product.name}
        </h3>
        {product.short_description && (
          <p className="mt-2 text-xs text-slate-500 line-clamp-2 flex-1">
            {product.short_description}
          </p>
        )}
        <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-xs">
          <span className="text-slate-400">{product.sku || "Ver detalle"}</span>
          <span className="text-pv-orange font-bold flex items-center gap-1 group-hover:gap-2 transition-all">
            Ficha técnica <ArrowRight size={12} />
          </span>
        </div>
      </div>
    </button>
  );
}

function EmptyState({ category }) {
  return (
    <div className="py-20 text-center max-w-md mx-auto">
      <div className="inline-flex w-16 h-16 items-center justify-center bg-pv-lime/15 rounded-sm mb-5">
        <Package className="text-pv-green" size={28} />
      </div>
      <h3 className="font-display font-bold text-pv-green text-2xl mb-2">
        Estamos cargando los {category.name.toLowerCase()}
      </h3>
      <p className="text-slate-500 mb-6">
        Pronto verás aquí toda nuestra línea. Mientras tanto, contáctenos para
        solicitar información personalizada de cualquier producto de esta
        categoría.
      </p>
      <Link
        to="/#contacto"
        data-testid="empty-cta-quote"
        className="inline-flex items-center gap-2 bg-pv-orange hover:bg-pv-orange/90 text-white font-semibold px-6 py-3 rounded-sm transition-colors"
      >
        Solicitar información <ArrowRight size={16} />
      </Link>
    </div>
  );
}
